import { useSortable } from '@dnd-kit/sortable'
import { ChevronDown, Copy, GripVertical, Trash2 } from 'lucide-react'
import type { LiveActivityRole } from '@/api/live'
import { Button } from '@/components/ui/button'
import { QuestionListEditor, fromQuestionDraft, toQuestionDraft } from './QuestionEditor'
import {
  ROLE_HINT, ROLE_LABEL, ROLES, TYPE_BLURB, TYPE_CHIP, TYPE_LABEL, summaryOf, type Draft,
} from './liveActivityDraft'

/** The number beside each row, joined to the next by a line, so the running order reads like a timeline. */
function OrderMarker({ n, last }: { n: number; last: boolean }) {
  return (
    <div className="flex flex-col items-center pt-3 shrink-0" aria-hidden="true">
      <span className="h-7 w-7 rounded-full bg-gray-800 text-white text-xs font-semibold flex items-center justify-center tabular-nums">{n}</span>
      {!last && <span className="flex-1 w-px bg-gray-200 mt-1" />}
    </div>
  )
}

/** Everything you can edit about one activity. */
function Editor({
  a, others, onChange, onDelete, onDuplicate, problem,
}: {
  a: Draft; others: Draft[]
  onChange: (next: Draft) => void; onDelete: () => void; onDuplicate: () => void; problem: string | null
}) {
  const hasQuestions = a.type === 'quiz' || a.type === 'confidence' || a.type === 'race'
  // Offer to copy another activity's questions (e.g. the pre-course quiz into the post-course one) so before and
  // after ask the same thing.
  const copyable = others.filter(o => o.type === a.type && o.questions.length > 0)

  return (
    <div className="border-t border-gray-100 px-4 py-4 space-y-4">
      <div className="space-y-2">
        <label className="block text-xs font-medium text-gray-500" htmlFor={`name-${a.key}`}>Name (what the trainer sees)</label>
        <input
          id={`name-${a.key}`}
          value={a.name}
          maxLength={80}
          placeholder="Name"
          onChange={e => onChange({ ...a, name: e.target.value })}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {(a.type === 'quiz' || a.type === 'confidence') && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <label className="text-xs font-medium text-gray-500" htmlFor={`role-${a.key}`}>Role</label>
            <select
              id={`role-${a.key}`}
              value={a.role ?? ''}
              onChange={e => onChange({ ...a, role: (e.target.value || null) as LiveActivityRole | null })}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 bg-white"
            >
              <option value="">{a.type === 'quiz' ? 'Stand-alone quiz' : 'Stand-alone check'}</option>
              {ROLES[a.type].map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        )}
        <p className="text-xs text-gray-400">{a.role ? ROLE_HINT[a.role] : TYPE_BLURB[a.type]}</p>
        {a.type === 'quiz' && !a.role && (
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={!!a.oneAtATime}
                onChange={e => onChange({ ...a, oneAtATime: e.target.checked })}
                className="rounded border-gray-300"
              />
              Ask one question at a time
            </label>
            {a.oneAtATime && (
              <p className="text-xs text-gray-400">
                The room answers each question together. You reveal everyone&apos;s answers, then move on to the next. Answers still count towards the booking&apos;s results.
              </p>
            )}
          </div>
        )}
      </div>

      {hasQuestions && (
        <>
          {copyable.length > 0 && a.questions.length === 0 && (
            <div className="text-xs text-gray-500 flex flex-wrap items-center gap-2">
              Copy questions from
              {copyable.map(o => (
                <button
                  key={o.key} type="button" className="text-indigo-600 hover:text-indigo-800 underline cursor-pointer"
                  onClick={() => onChange({ ...a, questions: o.questions.map(q => ({ ...toQuestionDraft(fromQuestionDraft(q)), id: q.id })) })}
                >{o.name || TYPE_LABEL[o.type]}</button>
              ))}
            </div>
          )}
          <QuestionListEditor questions={a.questions} ratingOnly={a.type === 'confidence'} raceMode={a.type === 'race'} onChange={questions => onChange({ ...a, questions })} />
        </>
      )}

      {a.type === 'race' && (
        <div className="space-y-1">
          <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
            Time limit per question
            <select
              value={a.timeLimitSeconds ?? ''}
              onChange={e => onChange({ ...a, timeLimitSeconds: e.target.value ? Number(e.target.value) : null })}
              className="rounded-md border border-gray-300 px-2 py-1 text-sm bg-white"
            >
              <option value="">No limit</option>
              {[...new Set([10, 15, 20, 30, 45, 60, ...(a.timeLimitSeconds ? [a.timeLimitSeconds] : [])])].sort((x, y) => x - y).map(n => <option key={n} value={n}>{n} seconds</option>)}
            </select>
          </label>
          <p className="text-xs text-gray-400">
            Each question starts with a 3-2-1 countdown, then reveals when everyone online has answered, when you choose, or at the time limit.
            Everyone who is right scores a point, and the three quickest score a bonus. Names show on the projector.
          </p>
        </div>
      )}

      {(a.type === 'wordcloud' || a.type === 'qa' || a.type === 'pulse') && (
        <div className="space-y-2">
          <input
            value={a.prompt ?? ''}
            maxLength={300}
            aria-label={a.type === 'wordcloud' ? 'Prompt' : 'Optional prompt'}
            placeholder={a.type === 'wordcloud' ? 'Prompt, e.g. One word for how you feel about first aid'
              : a.type === 'pulse' ? 'Optional wording. Leave blank for “How are you following so far?”'
              : 'Optional prompt, e.g. Anything you want to ask?'}
            onChange={e => onChange({ ...a, prompt: e.target.value })}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {a.type === 'wordcloud' && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              Words per person
              <select
                value={a.maxWords ?? 3}
                onChange={e => onChange({ ...a, maxWords: Number(e.target.value) })}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm bg-white"
              >
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          )}
        </div>
      )}

      {problem && <p className="text-xs text-amber-700">{problem}</p>}

      <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
        <Button type="button" variant="ghost" size="sm" className="gap-1.5 text-gray-600" onClick={onDuplicate}>
          <Copy size={14} /> Duplicate
        </Button>
        <Button type="button" variant="ghost" size="sm" className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={onDelete}>
          <Trash2 size={14} /> Delete
        </Button>
      </div>
    </div>
  )
}

/**
 * One activity in the course's running order: a compact row when closed (number, type, name, role and a one-line
 * summary), the full editor when open. The whole top of the card drags it to a new place in the order (a plain click still
 * opens or closes it, because a drag only starts once the pointer has moved); the grip is the keyboard handle. The bin
 * deletes it, open or closed. The editor below is not draggable, so typing and selecting text in it work normally.
 */
export function ActivityRow({
  a, index, last, open, dragging, others, problem, onToggle, onChange, onDelete, onDuplicate,
}: {
  a: Draft; index: number; last: boolean
  open: boolean
  /** Some row is being dragged: show every row closed so the list is short and easy to drop into. */
  dragging: boolean
  others: Draft[]; problem: string | null
  onToggle: () => void; onChange: (next: Draft) => void; onDelete: () => void; onDuplicate: () => void
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: a.key })
  const showBody = open && !dragging

  // Both delete buttons (the bin on the card and the one in the editor) ask first. Nothing is saved until Save, so Discard also undoes it.
  const confirmDelete = () => {
    const name = a.name.trim() || 'this activity'
    const n = a.questions.length
    const detail = n > 0 ? ` Its ${n} ${n === 1 ? 'question' : 'questions'} will be removed too.` : ''
    if (confirm(`Delete "${name}"?${detail} This takes effect when you save the list.`)) onDelete()
  }

  return (
    <li
      ref={setNodeRef}
      id={`activity-${a.key}`}
      style={{ transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined, transition, zIndex: isDragging ? 20 : undefined, position: 'relative' }}
      className="flex gap-3"
    >
      <OrderMarker n={index + 1} last={last} />
      <div className="flex-1 min-w-0 pb-3">
        <div className={`rounded-xl border bg-white shadow-sm transition-shadow ${problem ? 'border-amber-300' : 'border-gray-200'} ${isDragging ? 'shadow-xl ring-2 ring-indigo-300' : ''}`}>
          <div
            className={`flex items-center gap-1 pr-2 rounded-t-xl ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            {...listeners}
          >
            <button
              type="button"
              ref={setActivatorNodeRef}
              {...attributes}
              aria-label={`Drag to reorder ${a.name || 'this activity'}`}
              title="Drag the card to reorder"
              className="touch-none shrink-0 p-3 text-gray-300 hover:text-gray-500 rounded-l-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
            >
              <GripVertical size={16} />
            </button>

            <button
              type="button"
              onClick={onToggle}
              aria-expanded={showBody}
              aria-controls={`editor-${a.key}`}
              className="flex-1 min-w-0 flex items-center gap-3 py-2.5 text-left cursor-pointer"
            >
              <span className={`hidden sm:inline-block shrink-0 w-32 text-center rounded-full text-xs font-medium px-2 py-0.5 ${TYPE_CHIP[a.type]}`}>{TYPE_LABEL[a.type]}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-gray-900 truncate">{a.name.trim() || 'Untitled activity'}</span>
                  {a.role && (
                    <span className="hidden sm:inline-block shrink-0 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">{ROLE_LABEL[a.role]}</span>
                  )}
                </span>
                <span className="block text-xs text-gray-500 truncate">
                  <span className="sm:hidden">{TYPE_LABEL[a.type]}{a.role ? ` · ${ROLE_LABEL[a.role]}` : ''} · </span>
                  {summaryOf(a)}
                </span>
              </span>
              {problem && (
                <span title={problem} className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">Needs attention</span>
              )}
              <ChevronDown size={16} className={`shrink-0 text-gray-400 transition-transform ${showBody ? 'rotate-180' : ''}`} />
            </button>

            <button
              type="button"
              onClick={confirmDelete}
              aria-label={`Delete ${a.name.trim() || 'this activity'}`}
              title="Delete this activity"
              className="shrink-0 rounded-md p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
            >
              <Trash2 size={15} />
            </button>
          </div>

          {showBody && (
            <div id={`editor-${a.key}`}>
              <Editor a={a} others={others} onChange={onChange} onDelete={confirmDelete} onDuplicate={onDuplicate} problem={problem} />
            </div>
          )}
        </div>
      </div>
    </li>
  )
}

/** The built-in feedback form, shown as the fixed last step until the course adds its own. */
export function BuiltInFeedbackRow({ index, questions, onCustomise }: { index: number; questions: string[]; onCustomise: () => void }) {
  return (
    <li className="flex gap-3">
      <OrderMarker n={index + 1} last />
      <div className="flex-1 min-w-0">
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/60 px-4 py-3 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2 min-w-0">
              <span className="font-medium text-gray-700 truncate">Built-in feedback form</span>
              <span className="shrink-0 rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-gray-500">Added at the end</span>
            </p>
            <p className="text-xs text-gray-500 truncate">{questions.join(' · ')}</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={onCustomise}>Customise</Button>
        </div>
      </div>
    </li>
  )
}
